import { useState, useEffect } from "react";
import { getFilters } from "../api/products";
import { sizesLabel, stripEmojis } from "../utils";

/**
 * ProductFilters — offcanvas filter drawer
 * Props:
 * - open: boolean
 * - onClose: function
 * - onFilterChange: function({ priceMin, priceMax, colors, sizes, product_types, genders, collections })
 * - collectionId: optional string
 */
const ProductFilters = ({
  open,
  onClose,
  onFilterChange,
  selectedFilters = {},
  collectionId = "",
}) => {
  const [data, setData] = useState(null);
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(5000);
  const [priceMinInput, setPriceMinInput] = useState("0");
  const [priceMaxInput, setPriceMaxInput] = useState("5000");
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);

  const getPriceBounds = (source = data) => {
    const minCandidate = Number(source?.price_range?.min);
    const maxCandidate = Number(source?.price_range?.max);

    return {
      min: Number.isFinite(minCandidate) ? minCandidate : 0,
      max: Number.isFinite(maxCandidate) ? maxCandidate : 5000,
    };
  };

  // ── Load filters from API ─────────────────────────────────
  useEffect(() => {
    getFilters(collectionId ? { collection_id: collectionId } : {}).then(
      (res) => {
        if (res?.status === "ok") {
          setData(res);
          const { min, max } = getPriceBounds(res);
          setPriceMin(min);
          setPriceMax(max);
          setPriceMinInput(String(min));
          setPriceMaxInput(String(max));
        }
      },
    );
  }, [collectionId]);

  useEffect(() => {
    if (!open) return;
    const { min: defaultMin, max: defaultMax } = getPriceBounds();
    const minVal = Number.isFinite(Number(selectedFilters.priceRange?.[0]))
      ? Number(selectedFilters.priceRange?.[0])
      : defaultMin;
    const maxVal = Number.isFinite(Number(selectedFilters.priceRange?.[1]))
      ? Number(selectedFilters.priceRange?.[1])
      : defaultMax;

    setPriceMin(minVal);
    setPriceMax(maxVal);
    setPriceMinInput(String(minVal));
    setPriceMaxInput(String(maxVal));
    setSelectedColors(selectedFilters.colors || []);
    setSelectedSizes(selectedFilters.sizes || []);
    setSelectedProductTypes(selectedFilters.product_types || []);
    setSelectedGenders(selectedFilters.genders || []);
    setSelectedCollections(selectedFilters.collections || []);
  }, [
    open,
    data,
    selectedFilters.priceRange,
    selectedFilters.colors,
    selectedFilters.sizes,
    selectedFilters.product_types,
    selectedFilters.genders,
    selectedFilters.collections,
  ]);

  if (!open) return null;

  // ── Deduplicate colors by hex value ──────────────────────
  const uniqueColors = data?.colors
    ? [...new Map(data.colors.map((c) => [c.color, c])).values()]
    : [];

  const emit = (overrides = {}) => {
    onFilterChange?.({
      priceMin,
      priceMax,
      colors: selectedColors,
      sizes: selectedSizes,
      product_types: selectedProductTypes,
      genders: selectedGenders,
      collections: selectedCollections,
      ...overrides,
    });
  };

  const toggleItem = (list, setList, value, key) => {
    const updated = list.includes(value)
      ? list.filter((v) => v !== value)
      : [...list, value];
    setList(updated);
    emit({ [key]: updated });
  };

  const handleClearAll = () => {
    const { min: minValue, max: maxValue } = getPriceBounds();
    setPriceMin(minValue);
    setPriceMax(maxValue);
    setPriceMinInput(String(minValue));
    setPriceMaxInput(String(maxValue));
    setSelectedColors([]);
    setSelectedSizes([]);
    setSelectedProductTypes([]);
    setSelectedGenders([]);
    setSelectedCollections([]);
    onFilterChange?.({
      priceMin: minValue,
      priceMax: maxValue,
      colors: [],
      sizes: [],
      product_types: [],
      genders: [],
      collections: [],
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 1055,
        }}
      />

      {/* Drawer */}
      <div
        className="offcanvas offcanvas-start canvas-filter show"
        style={{ visibility: "visible", zIndex: 1060 }}
        aria-modal="true"
        role="dialog"
      >
        <div className="canvas-wrapper">
          {/* Header */}
          <header className="canvas-header">
            <div className="filter-icon">
              <span className="icon icon-filter" />
              <span>Filter</span>
            </div>
            <span
              className="icon-close icon-close-popup"
              onClick={onClose}
              style={{ cursor: "pointer" }}
            />
          </header>

          <div className="canvas-body">
            {/* ── Product Types (API) ── */}
            {data?.product_types?.length > 0 && (
              <div className="widget-facet">
                <div className="facet-title">
                  <span>Product Types</span>
                  <span className="icon icon-arrow-up" />
                </div>
                <ul className="list-categoris current-scrollbar mb_36">
                  {data.product_types.map((type) => {
                    const value =
                      typeof type === "string"
                        ? type
                        : type.slug ||
                          type.product_type ||
                          type.value ||
                          type.name ||
                          "";
                    const label =
                      typeof type === "string"
                        ? type
                        : type.name ||
                          type.title ||
                          type.label ||
                          type.product_type ||
                          type.value ||
                          "Product type";
                    const count =
                      typeof type === "string"
                        ? null
                        : (type.count ??
                          type.products_count ??
                          type.product_count ??
                          type.total ??
                          null);
                    return (
                      <li
                        key={value || label}
                        className={`cate-item${selectedProductTypes.includes(value) ? " current" : ""}`}
                        onClick={() =>
                          toggleItem(
                            selectedProductTypes,
                            setSelectedProductTypes,
                            value,
                            "product_types",
                          )
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <a href="#" onClick={(e) => e.preventDefault()}>
                          <span>{label}</span>
                          {count !== null && count !== undefined ? (
                            <>
                              &nbsp;<span>({count})</span>
                            </>
                          ) : null}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* ── Collections (API) ── */}
            {data?.collections?.length > 0 && (
              <div className="widget-facet">
                <div className="facet-title">
                  <span>Collections</span>
                  <span className="icon icon-arrow-up" />
                </div>
                <ul className="list-categoris current-scrollbar mb_36">
                  {data.collections.map((collection) => (
                    <li
                      key={collection.id}
                      className={`cate-item${selectedCollections.includes(collection.slug) ? " current" : ""}`}
                      onClick={() =>
                        toggleItem(
                          selectedCollections,
                          setSelectedCollections,
                          collection.slug,
                          "collections",
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <a href="#" onClick={(e) => e.preventDefault()}>
                        <span>
                          {stripEmojis(
                            collection.name || collection.slug || "",
                          )}
                        </span>
                        &nbsp;
                        <span>({collection.count})</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ── Genders (API) ── */}
            {/* {data?.genders?.length > 0 && (
              <div className="widget-facet">
                <div className="facet-title">
                  <span style={{ textTransform: "none" }}>Gender</span>
                  <span className="icon icon-arrow-up" />
                </div>
                <ul className="list-categoris current-scrollbar mb_36">
                  {data.genders.map((gender) => {
                    const label =
                      typeof gender === "string"
                        ? gender.charAt(0).toUpperCase() + gender.slice(1)
                        : gender?.name ||
                          gender?.label ||
                          gender?.value ||
                          "Gender";
                    const value =
                      typeof gender === "string"
                        ? gender
                        : gender?.value ||
                          gender?.slug ||
                          gender?.name ||
                          gender?.label ||
                          "";
                    return (
                      <li
                        key={value || label}
                        className={`cate-item${selectedGenders.includes(value) ? " current" : ""}`}
                        onClick={() =>
                          toggleItem(
                            selectedGenders,
                            setSelectedGenders,
                            value,
                            "genders",
                          )
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <a href="#" onClick={(e) => e.preventDefault()}>
                          <span>{label}</span>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )} */}

            {/* ── Price (from API range) ── */}
            <div className="widget-facet">
              <div className="facet-title">
                <span>Price</span>
                <span className="icon icon-arrow-up" />
              </div>
              <div className="widget-price filter-price mb_36">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  <span className="title-price">Price:</span>
                  <span
                    style={{ fontSize: 13, color: "#2c3a34", fontWeight: 600 }}
                  >
                    ₹{priceMin} — ₹{priceMax}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        fontSize: 11,
                        color: "#aaa",
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Min ₹
                    </label>
                    <input
                      type="number"
                      value={priceMinInput}
                      min={Number(data?.price_range?.min) || 0}
                      max={priceMax}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPriceMinInput(value);
                        if (value === "") return;
                        const val = Number(value);
                        if (Number.isFinite(val)) {
                          setPriceMin(val);
                          emit({ priceMin: val });
                        }
                      }}
                      style={{
                        width: "100%",
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontSize: 13,
                        color: "#2c3a34",
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        fontSize: 11,
                        color: "#aaa",
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Max ₹
                    </label>
                    <input
                      type="number"
                      value={priceMaxInput}
                      min={priceMin}
                      max={Number(data?.price_range?.max) || 9999}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPriceMaxInput(value);
                        if (value === "") return;
                        const val = Number(value);
                        if (Number.isFinite(val)) {
                          setPriceMax(val);
                          emit({ priceMax: val });
                        }
                      }}
                      style={{
                        width: "100%",
                        border: "1px solid #ddd",
                        padding: "8px",
                        fontSize: 13,
                        color: "#2c3a34",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Colors (API — hex values as circles) ── */}
            {uniqueColors.length > 0 && (
              <div className="widget-facet">
                <div className="facet-title">
                  <span>Color</span>
                  <span className="icon icon-arrow-up" />
                </div>
                <ul className="tf-filter-group filter-color current-scrollbar mb_36">
                  {uniqueColors.map(({ color, name, count }) => (
                    <li
                      key={color}
                      className="list-item d-flex gap-12 align-items-center"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        toggleItem(
                          selectedColors,
                          setSelectedColors,
                          color,
                          "colors",
                        )
                      }
                    >
                      {/* Color circle */}
                      <span
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor: color,
                          border: selectedColors.includes(color)
                            ? "3px solid #2c3a34"
                            : "1.5px solid #ccc",
                          flexShrink: 0,
                          display: "inline-block",
                          boxShadow:
                            color === "#ffffff"
                              ? "inset 0 0 0 1px #ddd"
                              : "none",
                        }}
                      />
                      <label className="label" style={{ cursor: "pointer" }}>
                        <span>{name || color}</span>&nbsp;
                        <span>({count})</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ── Sizes (API) ── */}
            {data?.sizes?.length > 0 && (
              <div className="widget-facet">
                <div className="facet-title">
                  <span>Size</span>
                  <span className="icon icon-arrow-up" />
                </div>
                <ul className="tf-filter-group current-scrollbar mb_36">
                  {data.sizes
                    .sort((a, b) => a - b)
                    .map(({ size, count, product_type }) => (
                      <li
                        key={size}
                        className="list-item d-flex gap-12 align-items-center"
                      >
                        <input
                          type="checkbox"
                          className="tf-check tf-check-size"
                          id={`size-${size}`}
                          checked={selectedSizes.includes(size)}
                          onChange={() =>
                            toggleItem(
                              selectedSizes,
                              setSelectedSizes,
                              size,
                              "sizes",
                            )
                          }
                        />
                        <label htmlFor={`size-${size}`} className="label">
                          <span>
                            {product_type} - {sizesLabel[size]}
                          </span>
                          &nbsp;
                          <span>({count})</span>
                        </label>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* ── Loading state ── */}
            {!data && (
              <div
                style={{
                  padding: "24px 0",
                  textAlign: "center",
                  color: "#888",
                  fontSize: 14,
                }}
              >
                Loading filters...
              </div>
            )}
            <div style={{ display: "flex", gap: 12, paddingTop: 8 }}>
              <button
                onClick={handleClearAll}
                className="tf-btn btn-outline animate-hover-btn w-100 justify-content-center"
              >
                Clear All
              </button>
              <button
                onClick={onClose}
                className="tf-btn btn-fill animate-hover-btn w-100 justify-content-center"
              >
                Apply
              </button>
            </div>
            {/* actions intentionally removed: filters update live */}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductFilters;
